import React, { useEffect, useMemo, useRef, useState } from "react";
import { GitBranch, ChevronRight } from "lucide-react";
import { api } from "../api";
import { Header } from "./Shell";

export function AlgorithmsPage() {
  const [algorithms, setAlgorithms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/algorithms")
      .then(r => setAlgorithms(r.data))
      .finally(() => setLoading(false));
  }, []);

  const openAlgorithm = async (id) => {
    const r = await api.get(`/algorithms/${id}`);
    setSelected(r.data);
  };

  if (selected) {
    return (
      <>
        <Header
          kicker="OPERATIVO / ALGORITMI"
          title={`Algoritmo n° ${selected.number}`}
          subtitle={selected.title}
        />

        <button
          className="row-btn"
          type="button"
          onClick={() => setSelected(null)}
        >
          ← Torna agli algoritmi
        </button>

        <AlgorithmFlow algorithm={selected} />
      </>
    );
  }

  return (
    <>
      <Header
        kicker="OPERATIVO / ALGORITMI"
        title="Algoritmi"
        subtitle="Consulta gli algoritmi operativi in formato flowchart."
      />

      <section className="algorithm-list">
        {loading && <p>Caricamento…</p>}

        {!loading && algorithms.length === 0 && (
          <div className="empty-row">
            Nessun algoritmo disponibile.
          </div>
        )}

        {algorithms.map(item => (
          <button
            key={item.id}
            className="algorithm-card"
            type="button"
            onClick={() => openAlgorithm(item.id)}
          >
            <div className="algorithm-icon">
              <GitBranch size={24} />
            </div>

            <div className="algorithm-card-content">
              <span className="eyebrow">
                ALGORITMO N° {item.number}
              </span>

              <h2>{item.title}</h2>

              <p>
                {item.document || ""}
                {item.attachment ? ` · ${item.attachment}` : ""}
                {item.revision ? ` · Rev. ${item.revision}` : ""}
              </p>
            </div>

            <ChevronRight size={20} />
          </button>
        ))}
      </section>
    </>
  );
}


/* =========================================================
   FLOWCHART
   ========================================================= */

export function AlgorithmFlow({
  algorithm,
  focusEdge = null,
  onNodeClick = null,
  selectedNodeId = null,
  editableEdgeIndex = null,
  onEdgePointMove = null,
  onSelectedEdgePointMove = null,
  onSelectedEdgePointAdd = null,
  onSelectedEdgePointClick = null,
  selectedEdgePointIndex = null,
  addingSelectedEdgePoint = false,
  onEdgePointAdd = null,
  selectedEdgeIndex = null,
  selectedEdgeLabelIndex = null,
  onEdgeClick = null,
  onEdgeLabelClick = null,
  onEdgeLabelMove = null,
  onEdgeContextMenu = null
}) { //l'export rende la funzione utilizzabile anche in pagina amministratore
  
  // Riferimento SVG per convertire le coordinate del mouse.
  const svgRef = useRef(null);
  const draggingPointRef = useRef(null);
  const draggingLabelRef = useRef(null);
  const [lockedViewBox, setLockedViewBox] = useState(null);

  // Posizione temporanea del mouse per le guide di allineamento.
  const [alignmentCursor, setAlignmentCursor] = useState(null);

  const trackAlignmentCursor = (event) => {
    const position = getSvgMousePosition(event);
    if (position) setAlignmentCursor(position);
  };

  const clearAlignmentCursor = () => {
    setAlignmentCursor(null);
  };

  const getSvgMousePosition = (event) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const matrix = svg.getScreenCTM();
    if (!matrix) return null;

    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;

    const transformed = point.matrixTransform(matrix.inverse());

    if (!Number.isFinite(transformed.x) ||
        !Number.isFinite(transformed.y)) {
      return null;
    }

    return {
      x: Math.round(transformed.x),
      y: Math.round(transformed.y)
    };
  };

  // Avvia il trascinamento di un punto intermedio.
  const startEdgePointDrag = (event, edgeIndex, pointIndex) => {
    const previewMode =
      editableEdgeIndex === edgeIndex && !!onEdgePointMove;
    const directMode =
      selectedEdgeIndex === edgeIndex && !!onSelectedEdgePointMove;

    if (
      (!previewMode && !directMode) ||
      (event.button !== undefined && event.button !== 0)
    ) return;

    event.preventDefault();
    event.stopPropagation();

    const svg = svgRef.current;
    if (!svg) return;

    const currentViewBox = svg.viewBox.baseVal;

    setLockedViewBox({
      x: currentViewBox.x,
      y: currentViewBox.y,
      width: currentViewBox.width,
      height: currentViewBox.height
    });

    draggingPointRef.current = {
      edgeIndex,
      pointIndex,
      pointerId: event.pointerId,
      captureElement: event.currentTarget
    };

    // Cattura del puntatore direttamente sul punto SVG.
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  // Avvia lo spostamento di un'etichetta.
  const startEdgeLabelDrag = (event, edgeIndex) => {
    if (!onEdgeLabelMove || event.button !== 0) return;

    const svg = svgRef.current;
    if (!svg) return;

    event.preventDefault();
    event.stopPropagation();

    const currentViewBox = svg.viewBox.baseVal;

    setLockedViewBox({
      x: currentViewBox.x,
      y: currentViewBox.y,
      width: currentViewBox.width,
      height: currentViewBox.height
    });

    clearAlignmentCursor();

    draggingLabelRef.current = {
      edgeIndex,
      pointerId: event.pointerId,
      captureElement: event.currentTarget
    };

    if (onEdgeLabelClick) {
      onEdgeLabelClick(edgeIndex);
    }

    // Catturiamo il puntatore direttamente sull'etichetta.
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  // Aggiorna le coordinate durante il trascinamento.
  const handleEdgePointerMove = (event) => {
    const draggingLabel = draggingLabelRef.current;

    if (draggingLabel && event.pointerId === draggingLabel.pointerId) {
      const position = getSvgMousePosition(event);
      if (position && onEdgeLabelMove) {
        onEdgeLabelMove(
          draggingLabel.edgeIndex,
          position.x,
          position.y
        );
      }
      return;
    }

    const dragging = draggingPointRef.current;
    if (!dragging || event.pointerId !== dragging.pointerId) return;

    const position = getSvgMousePosition(event);
    if (!position) return;

    setAlignmentCursor(position);

    if (
      selectedEdgeIndex === dragging.edgeIndex &&
      onSelectedEdgePointMove
    ) {
      onSelectedEdgePointMove(
        dragging.edgeIndex,
        dragging.pointIndex,
        position.x,
        position.y
      );
    } else if (
      editableEdgeIndex === dragging.edgeIndex &&
      onEdgePointMove
    ) {
      onEdgePointMove(
        dragging.pointIndex,
        position.x,
        position.y
      );
    }
  };

  // Termina il trascinamento di un'etichetta.
  const stopEdgeLabelDrag = (event) => {
    const dragging = draggingLabelRef.current;
    if (!dragging || event.pointerId !== dragging.pointerId) return;

    draggingLabelRef.current = null;
    setLockedViewBox(null);

    const captureElement = dragging.captureElement;
    if (captureElement &&
        captureElement.hasPointerCapture(event.pointerId)) {
      captureElement.releasePointerCapture(event.pointerId);
    }
  };

  // Termina il trascinamento.
  const stopEdgePointDrag = (event) => {
    if (draggingLabelRef.current) {
      stopEdgeLabelDrag(event);
      return;
    }

    const dragging = draggingPointRef.current;
    if (!dragging || event.pointerId !== dragging.pointerId) return;

    draggingPointRef.current = null;
    setLockedViewBox(null);
    clearAlignmentCursor();

    const captureElement = dragging.captureElement;
    if (
      captureElement &&
      captureElement.hasPointerCapture(event.pointerId)
    ) {
      captureElement.releasePointerCapture(event.pointerId);
    }
  };

  // Inserisce un punto rispettando il percorso realmente disegnato.
  const handleEdgePointAdd = (event, edge, edgeIndex) => {
    const previewMode =
      editableEdgeIndex === edgeIndex && !!onEdgePointAdd;
    const directMode =
      selectedEdgeIndex === edgeIndex &&
      addingSelectedEdgePoint &&
      !!onSelectedEdgePointAdd;

    if (!previewMode && !directMode) return;

    event.preventDefault();
    event.stopPropagation();

    const position = getSvgMousePosition(event);
    if (!position) return;

    const source = nodeMap[edge.source];
    const target = nodeMap[edge.target];
    if (!source || !target) return;

    const sourceSide = edge.sourceSide || "bottom";
    const targetSide = edge.targetSide || "top";

    const start = getAnchor(source, sourceSide);
    const end = getAnchor(target, targetSide);

    const existingPoints = Array.isArray(edge.points)
      ? edge.points
      : [];

    let basePoints = existingPoints;

    // Se non ci sono punti, conserviamo le svolte automatiche.
    if (existingPoints.length === 0) {
      if (
        sourceSide === "left" ||
        sourceSide === "right" ||
        targetSide === "left" ||
        targetSide === "right"
      ) {
        const middleX = start.x + (end.x - start.x) / 2;

        basePoints = [
          { x: middleX, y: start.y },
          { x: middleX, y: end.y }
        ];
      } else {
        const middleY = start.y + (end.y - start.y) / 2;

        basePoints = [
          { x: start.x, y: middleY },
          { x: end.x, y: middleY }
        ];
      }
    }

    const route = [start, ...basePoints, end];

    let insertIndex = 0;
    let minDistance = Infinity;

    for (let i = 0; i < route.length - 1; i += 1) {
      const a = route[i];
      const b = route[i + 1];

      const dx = Number(b.x) - Number(a.x);
      const dy = Number(b.y) - Number(a.y);
      const lengthSquared = dx * dx + dy * dy;

      const t = lengthSquared === 0
        ? 0
        : Math.max(0, Math.min(1,
            ((position.x - Number(a.x)) * dx +
             (position.y - Number(a.y)) * dy) / lengthSquared
          ));

      const closestX = Number(a.x) + t * dx;
      const closestY = Number(a.y) + t * dy;

      const distance =
        (position.x - closestX) ** 2 +
        (position.y - closestY) ** 2;

      if (distance < minDistance) {
        minDistance = distance;
        insertIndex = i;
      }
    }

    if (directMode) {
      onSelectedEdgePointAdd(
        edgeIndex,
        insertIndex,
        position.x,
        position.y,
        basePoints
      );
    } else {
      onEdgePointAdd(
        insertIndex,
        position.x,
        position.y,
        basePoints
      );
    }
  };

  const nodes = useMemo(
  () => algorithm.nodes || [],
  [algorithm.nodes]
);

const edges = useMemo(
  () => algorithm.edges || [],
  [algorithm.edges]
);

  /*
   * Compatibilità con i dati già presenti in MongoDB:
   * se width/height non esistono utilizziamo 260 x 100.
   */
  const getNodeSize = (node) => ({
    width: Number(node.width) || 260,
    height: Number(node.height) || 100
  });

  const nodeMap = useMemo(
    () => Object.fromEntries(nodes.map(node => [node.id, node])),
    [nodes]
  );

  // Riferimenti geometrici per le guide di allineamento.
  const alignmentReferences = useMemo(() => {
    const vertical = [];
    const horizontal = [];

    nodes.forEach(node => {
      const x = Number(node.x) || 0;
      const y = Number(node.y) || 0;
      const width = Number(node.width) || 260;
      const height = Number(node.height) || 100;

      vertical.push(x, x + width / 2, x + width);
      horizontal.push(y, y + height / 2, y + height);
    });

    return { vertical, horizontal };
  }, [nodes]);

  const alignmentGuides = useMemo(() => {
    if (!alignmentCursor) return null;

    const tolerance = 8;

    const nearest = (values, coordinate) => {
      let match = null;
      let distance = Infinity;

      values.forEach(value => {
        const delta = Math.abs(value - coordinate);
        if (delta <= tolerance && delta < distance) {
          match = value;
          distance = delta;
        }
      });

      return match;
    };

    const x = nearest(
      alignmentReferences.vertical,
      alignmentCursor.x
    );

    const y = nearest(
      alignmentReferences.horizontal,
      alignmentCursor.y
    );

    // Nessun riferimento vicino: non renderizzare
    // nemmeno il contenitore SVG delle guide.
    if (x === null && y === null) {
      return null;
    }

    return { x, y };
  }, [alignmentCursor, alignmentReferences]);


  /* ---------------------------------------------------------
     PUNTI DI CONNESSIONE
     --------------------------------------------------------- */

  const getAnchor = (node, side = "bottom") => {
    const { width, height } = getNodeSize(node);

    const x = Number(node.x) || 0;
    const y = Number(node.y) || 0;

    switch (side) {
      case "top":
        return {
          x: x + width / 2,
          y
        };

      case "right":
        return {
          x: x + width,
          y: y + height / 2
        };

      case "left":
        return {
          x,
          y: y + height / 2
        };

      case "bottom":
      default:
        return {
          x: x + width / 2,
          y: y + height
        };
    }
  };


  /* ---------------------------------------------------------
     PERCORSO FRECCE
     --------------------------------------------------------- */

  const getPath = (edge) => {
    const source = nodeMap[edge.source];
    const target = nodeMap[edge.target];

    if (!source || !target) return "";

    const sourceSide = edge.sourceSide || "bottom";
    const targetSide = edge.targetSide || "top";

    const start = getAnchor(source, sourceSide);
    const end = getAnchor(target, targetSide);

    /*
     * Se l'edge contiene punti intermedi, li rispettiamo
     * esattamente.
     *
     * Esempio MongoDB:
     *
     * points: [
     *   { x: 400, y: 300 },
     *   { x: 700, y: 300 }
     * ]
     */
    if (Array.isArray(edge.points) && edge.points.length > 0) {
      const segments = [`M ${start.x} ${start.y}`];

      edge.points.forEach(point => {
        segments.push(
          `L ${Number(point.x)} ${Number(point.y)}`
        );
      });

      segments.push(`L ${end.x} ${end.y}`);

      return segments.join(" ");
    }

    /*
     * Percorso automatico.
     */

    if (
      sourceSide === "left" ||
      sourceSide === "right" ||
      targetSide === "left" ||
      targetSide === "right"
    ) {
      const middleX = start.x + (end.x - start.x) / 2;

      return [
        `M ${start.x} ${start.y}`,
        `L ${middleX} ${start.y}`,
        `L ${middleX} ${end.y}`,
        `L ${end.x} ${end.y}`
      ].join(" ");
    }

    const middleY = start.y + (end.y - start.y) / 2;

    return [
      `M ${start.x} ${start.y}`,
      `L ${start.x} ${middleY}`,
      `L ${end.x} ${middleY}`,
      `L ${end.x} ${end.y}`
    ].join(" ");
  };


  /* ---------------------------------------------------------
     POSIZIONE ETICHETTA SÌ / NO
     --------------------------------------------------------- */

  const getEdgeLabelPosition = (edge) => {
    /*
     * Possiamo specificare manualmente anche la posizione
     * dell'etichetta.
     */
    if (
      edge.labelX !== undefined &&
      edge.labelY !== undefined
    ) {
      return {
        x: Number(edge.labelX),
        y: Number(edge.labelY)
      };
    }

    const source = nodeMap[edge.source];
    const target = nodeMap[edge.target];

    if (!source || !target) {
      return { x: 0, y: 0 };
    }

    const start = getAnchor(
      source,
      edge.sourceSide || "bottom"
    );

    const end = getAnchor(
      target,
      edge.targetSide || "top"
    );

    return {
      x: start.x + (end.x - start.x) / 2 + 8,
      y: start.y + (end.y - start.y) / 2 - 7
    };
  };


  /* ---------------------------------------------------------
     DISEGNO NODO
     --------------------------------------------------------- */

  const renderNode = (node) => {
    const x = Number(node.x) || 0;
    const y = Number(node.y) || 0;

    // Se non è presente un colore valido, resta lo stile CSS originale.
    const customColor =
      typeof node.color === "string" &&
      /^#[0-9a-fA-F]{6}$/.test(node.color)
        ? node.color
        : null;

    const nodeStyle = customColor
      ? { fill: customColor }
      : undefined;

    // Contrasto automatico del testo per i colori personalizzati.
    const textStyle = customColor
      ? (() => {
          const r = parseInt(customColor.slice(1, 3), 16);
          const g = parseInt(customColor.slice(3, 5), 16);
          const b = parseInt(customColor.slice(5, 7), 16);
          const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
          return { color: luminance > 0.55 ? "#212529" : "#ffffff" };
        })()
      : undefined;

    const { width, height } = getNodeSize(node);

    /*
     * ROMBO DECISIONALE
     */
    if (node.type === "decision") {
      const cx = x + width / 2;
      const cy = y + height / 2;

      return (
        <g
          key={node.id}
          onClick={onNodeClick ? (event) => {
            event.stopPropagation();
            onNodeClick(node.id);
          } : undefined}
          style={onNodeClick ? { cursor: "pointer" } : undefined}
          opacity={selectedNodeId && selectedNodeId !== node.id ? 0.65 : 1}
        >
          <polygon
            className="flow-node flow-decision"
            style={nodeStyle}
            points={[
              `${cx},${y}`,
              `${x + width},${cy}`,
              `${cx},${y + height}`,
              `${x},${cy}`
            ].join(" ")}
          />

          <foreignObject
            x={x + width * 0.17}
            y={y + height * 0.18}
            width={width * 0.66}
            height={height * 0.64}
          >
            <div
              xmlns="http://www.w3.org/1999/xhtml"
              className="flow-node-text flow-decision-text"
              style={textStyle}
            >
              {node.text}
            </div>
          </foreignObject>
        </g>
      );
    }


    /*
     * TUTTI GLI ALTRI NODI
     */
    const radius =
      node.type === "entry" || node.type === "exit"
        ? Math.min(height / 2, 45)
        : 8;

    return (
      <g
          key={node.id}
          onClick={onNodeClick ? (event) => {
            event.stopPropagation();
            onNodeClick(node.id);
          } : undefined}
          style={onNodeClick ? { cursor: "pointer" } : undefined}
          opacity={selectedNodeId && selectedNodeId !== node.id ? 0.65 : 1}
        >
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={radius}
          className={`flow-node flow-${node.type}`}
          style={nodeStyle}
        />

        <foreignObject
          x={x + 12}
          y={y + 8}
          width={width - 24}
          height={height - 16}
        >
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            className="flow-node-text"
            style={textStyle}
          >
            {node.text}
          </div>
        </foreignObject>
      </g>
    );
  };


  /* ---------------------------------------------------------
     DIMENSIONI AUTOMATICHE DEL FOGLIO SVG
     --------------------------------------------------------- */

  const canvasSize = useMemo(() => {
    if (nodes.length === 0) {
      return {
        width: 1150,
        height: 800
      };
    }

    let maxX = 0;
    let maxY = 0;

    nodes.forEach(node => {
      const { width, height } = getNodeSize(node);

      maxX = Math.max(
        maxX,
        (Number(node.x) || 0) + width
      );

      maxY = Math.max(
        maxY,
        (Number(node.y) || 0) + height
      );
    });

    /*
     * Consideriamo anche eventuali punti intermedi
     * delle frecce.
     */
    edges.forEach(edge => {
      if (!Array.isArray(edge.points)) return;

      edge.points.forEach(point => {
        maxX = Math.max(maxX, Number(point.x) || 0);
        maxY = Math.max(maxY, Number(point.y) || 0);
      });
    });

    return {
      width: Math.max(1150, maxX + 100),
      height: Math.max(800, maxY + 100)
    };
  }, [nodes, edges]);
  /*
 * Area visualizzata dall'SVG.
 *
 * Normalmente coincide con l'intera flowchart.
 * Se focusEdge è presente, mostriamo invece una zona
 * circoscritta al collegamento in modifica.
 */
const viewBox = useMemo(() => {
  if (!focusEdge) {
    return {
      x: 0,
      y: 0,
      width: canvasSize.width,
      height: canvasSize.height
    };
  }

  const source = nodeMap[focusEdge.source];
  const target = nodeMap[focusEdge.target];

  if (!source || !target) {
    return {
      x: 0,
      y: 0,
      width: canvasSize.width,
      height: canvasSize.height
    };
  }

  const sourceSize = getNodeSize(source);
  const targetSize = getNodeSize(target);

  const xs = [
    Number(source.x) || 0,
    (Number(source.x) || 0) + sourceSize.width,
    Number(target.x) || 0,
    (Number(target.x) || 0) + targetSize.width
  ];

  const ys = [
    Number(source.y) || 0,
    (Number(source.y) || 0) + sourceSize.height,
    Number(target.y) || 0,
    (Number(target.y) || 0) + targetSize.height
  ];

  (focusEdge.points || []).forEach(point => {
    xs.push(Number(point.x) || 0);
    ys.push(Number(point.y) || 0);
  });

  const padding = 100;

  const minX = Math.min(...xs) - padding;
  const maxX = Math.max(...xs) + padding;

  const minY = Math.min(...ys) - padding;
  const maxY = Math.max(...ys) + padding;

  return {
    x: Math.max(0, minX),
    y: Math.max(0, minY),
    width: Math.max(300, maxX - Math.max(0, minX)),
    height: Math.max(250, maxY - Math.max(0, minY))
  };
}, [focusEdge, nodeMap, canvasSize]);


  /* ---------------------------------------------------------
     ALGORITMO SENZA FLOWCHART
     --------------------------------------------------------- */

  if (nodes.length === 0) {
    return (
      <section className="algorithm-flow">
        <AlgorithmSource algorithm={algorithm} />

        <div className="flow-empty">
          <GitBranch size={40} />

          <h2>{algorithm.title}</h2>

          <p>
            La flowchart di questo algoritmo non è ancora
            disponibile.
          </p>
        </div>
      </section>
    );
  }


  /* ---------------------------------------------------------
     FLOWCHART
     --------------------------------------------------------- */

  return (
    <section className="algorithm-flow">

      <AlgorithmSource algorithm={algorithm} />

      <div className="flow-scroll">
        <svg
          ref={svgRef}
          className="flow-chart"
          onPointerMove={handleEdgePointerMove}
          onPointerUp={stopEdgePointDrag}
          onPointerCancel={stopEdgePointDrag}
          onPointerLeave={() => {
            if (!draggingPointRef.current) clearAlignmentCursor();
          }}
          viewBox={`${(lockedViewBox || viewBox).x} ${(lockedViewBox || viewBox).y} ${(lockedViewBox || viewBox).width} ${(lockedViewBox || viewBox).height}`}
          preserveAspectRatio="xMidYMin meet"
          role="img"
          aria-label={
            `Flowchart algoritmo ${algorithm.number}: ${algorithm.title}`
          }
        >

          <defs>
            <marker
              id="algorithm-arrow"
              viewBox="0 0 10 10"
              refX="9"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" />
            </marker>
          </defs>


          {/* FRECCE - vengono disegnate prima dei nodi */}
          <g className="flow-edges">

            {edges.map((edge, edgeIndex) => {
              const labelPosition =
                getEdgeLabelPosition(edge);

              return (
                <g key={edge.id ?? `${edge.source}-${edge.target}-${edgeIndex}`}>

                  <path
                    d={getPath(edge)}
                    className="flow-edge"
                    markerEnd="url(#algorithm-arrow)"
                    onClick={event => {
                      if (!onEdgeClick) return;
                      event.stopPropagation();
                      onEdgeClick(edgeIndex);
                    }}
                    onContextMenu={event => {
                      if (!onEdgeContextMenu) return;
                      event.preventDefault();
                      event.stopPropagation();
                      const position = getSvgMousePosition(event);
                      if (position) {
                        onEdgeContextMenu(edgeIndex, position);
                      }
                    }}
                    style={{
                      cursor: onEdgeClick || onEdgeContextMenu
                        ? "pointer"
                        : undefined,
                      strokeWidth: selectedEdgeIndex === edgeIndex
                        ? 4
                        : undefined
                    }}
                  />

                  {/* Area invisibile per la selezione dei collegamenti.
                      Non interferisce con l'editor dei punti intermedi. */}
                  {(onEdgeClick || onEdgeContextMenu) &&
                    editableEdgeIndex !== edgeIndex &&
                    !(
                      selectedEdgeIndex === edgeIndex &&
                      addingSelectedEdgePoint &&
                      onSelectedEdgePointAdd
                    ) && (
                    <path
                      d={getPath(edge)}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={18}
                      pointerEvents="stroke"
                      style={{ cursor: "pointer" }}
                      onClick={event => {
                        if (!onEdgeClick) return;
                        event.stopPropagation();
                        onEdgeClick(edgeIndex);
                      }}
                      onContextMenu={event => {
                        if (!onEdgeContextMenu) return;
                        event.preventDefault();
                        event.stopPropagation();

                        const position = getSvgMousePosition(event);
                        if (position) {
                          onEdgeContextMenu(edgeIndex, position);
                        }
                      }}
                    />
                  )}

                  {(
                    (editableEdgeIndex === edgeIndex && onEdgePointAdd) ||
                    (
                      selectedEdgeIndex === edgeIndex &&
                      addingSelectedEdgePoint &&
                      onSelectedEdgePointAdd
                    )
                  ) && (
                      <path
                        d={getPath(edge)}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={18}
                        pointerEvents="stroke"
                        style={{ cursor: "crosshair" }}
                        onPointerMove={trackAlignmentCursor}
                        onPointerLeave={clearAlignmentCursor}
                        onClick={event => {
                          clearAlignmentCursor();
                          handleEdgePointAdd(event, edge, edgeIndex);
                        }}
                      />
                    )}

                  {edge.label && (
                    <text
                      x={labelPosition.x}
                      y={labelPosition.y}
                      className="flow-edge-label"
                      onPointerDown={event =>
                        startEdgeLabelDrag(event, edgeIndex)
                      }
                      onClick={event => {
                        if (!onEdgeLabelClick) return;
                        event.stopPropagation();
                        onEdgeLabelClick(edgeIndex);
                      }}
                      style={{
                        cursor: onEdgeLabelMove
                          ? "grab"
                          : onEdgeLabelClick
                            ? "pointer"
                            : undefined,
                        touchAction: onEdgeLabelMove ? "none" : undefined,
                        fontWeight: selectedEdgeLabelIndex === edgeIndex
                          ? "bold"
                          : undefined
                      }}
                    >
                      {edge.label}
                    </text>
                  )}

                  {(
                    (editableEdgeIndex === edgeIndex && onEdgePointMove) ||
                    (selectedEdgeIndex === edgeIndex && onSelectedEdgePointMove)
                  ) &&
                    (edge.points || []).map((point, pointIndex) => (
                      <circle
                        key={`point-${pointIndex}`}
                        cx={Number(point.x)}
                        cy={Number(point.y)}
                        r={8}
                        fill={
                          selectedEdgeIndex === edgeIndex &&
                          selectedEdgePointIndex === pointIndex
                            ? "#dbeafe"
                            : "#ffffff"
                        }
                        stroke={
                          selectedEdgeIndex === edgeIndex &&
                          selectedEdgePointIndex === pointIndex
                            ? "#1d4ed8"
                            : "#2563eb"
                        }
                        strokeWidth={3}
                        style={{
                          cursor: "grab",
                          touchAction: "none"
                        }}
                        onPointerDown={event => {
                          if (
                            onSelectedEdgePointClick &&
                            selectedEdgeIndex === edgeIndex
                          ) {
                            onSelectedEdgePointClick(edgeIndex, pointIndex);
                          }
                          startEdgePointDrag(event, edgeIndex, pointIndex);
                        }}
                        onClick={event => event.stopPropagation()}
                      />
                    ))}

                </g>
              );
            })}

          </g>


          {/* Guide temporanee di allineamento dei punti intermedi.
              Non intercettano mai gli eventi del mouse. */}
          {alignmentGuides && (
            <g
              className="flow-alignment-guides"
              pointerEvents="none"
              aria-hidden="true"
            >
              {alignmentGuides.x !== null && (
                <line
                  x1={alignmentGuides.x}
                  y1={viewBox.y}
                  x2={alignmentGuides.x}
                  y2={viewBox.y + viewBox.height}
                  stroke="#0891b2"
                  strokeWidth={1.5}
                  strokeDasharray="6 5"
                  opacity={0.8}
                />
              )}
              {alignmentGuides.y !== null && (
                <line
                  x1={viewBox.x}
                  y1={alignmentGuides.y}
                  x2={viewBox.x + viewBox.width}
                  y2={alignmentGuides.y}
                  stroke="#0891b2"
                  strokeWidth={1.5}
                  strokeDasharray="6 5"
                  opacity={0.8}
                />
              )}
            </g>
          )}

          {/* NODI */}
          <g className="flow-nodes">
            {nodes.map(renderNode)}
          </g>

        </svg>
      </div>


      <div className="flow-legend">

        <span>
          <i className="legend-box legend-decision" />
          Decisione
        </span>

        <span>
          <i className="legend-box legend-treatment" />
          Trattamento
        </span>

        <span>
          <i className="legend-box legend-warning" />
          Rivalutazione
        </span>

      </div>

    </section>
  );
}


/* =========================================================
   DATI DOCUMENTO
   ========================================================= */

function AlgorithmSource({ algorithm }) {
  return (
    <div className="algorithm-source">

      <b>{algorithm.document}</b>

      {algorithm.attachment && (
        <span>{algorithm.attachment}</span>
      )}

      {algorithm.revision && (
        <span>Rev. {algorithm.revision}</span>
      )}

    </div>
  );
}